/**
 * The terminal wall: a tile, its handles, and the terminal inside it.
 *
 * Lifted out of the cockpit when a second screen needed it. The SSH console shows
 * shells on machines rather than agents on projects, but a tile is a tile: the
 * same drag handle, the same span corner, the same visibility gate, the same
 * lease rule that only the focused pane takes the keyboard. Two copies of this
 * would have drifted within a week — the wall has already collected four
 * measured defects, and a copy inherits them without inheriting their fixes.
 *
 * What stayed behind in the cockpit is everything about *agents*: accounts,
 * models, safe boundaries, the waiting signal. This file knows only that a
 * session has an id, a host, a state and a name.
 */

import { useCallback, useEffect, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSortable } from '@dnd-kit/sortable'
import { GripVertical, LayoutGrid, Maximize2, Minimize2, Plus, Radio, RadioReceiver, Trash2, X } from 'lucide-react'
import clsx from 'clsx'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'

import { api, type PageResponse } from '@/api/client'
import { Icon } from '@/components/icon'
import { Button, StatusDot } from '@/components/primitives'
import { Notice, Dialog, Segmented, TextField } from '@/components/controls'
import { useT, useLocale } from '@/i18n/context'
import { formatDuration, formatRelative } from '@/i18n'
import { useElapsed, useNow } from '@/hooks/useElapsed'
import { sessionActivityTone } from '@/lib/tone'
import { activityLabelKey, sessionActivity, type SessionActivity } from '@/lib/sessionActivity'
import { useOnceVisible } from '@/hooks/useOnceVisible'
import { useBroadcast } from './broadcast'
import { usePaneChord } from './paneChord'
import { SPAN_MAX, type LayoutMode, type WallPane, type WallPreset } from '@/hooks/useWallLayout'
import { dragId } from '@/hooks/useManualOrder'
import { TerminalPane, type TerminalMode } from '@/terminal/TerminalPane'
import type { ID, ISOTime, Provider, SessionKind, SessionState } from '@/types/domain'
import { keyboardIsInAField } from '@/lib/keyboardOwner'

/**
 * What a session last did — api.LastTurnResponse, read from its transcript by
 * the server. Every field is absent when unknown, and the whole block is absent
 * when the session has no readable transcript: that is "unmeasured", never "did
 * nothing", and it is rendered as such. `toolErrors` is absent for a provider
 * whose record does not mark failures (Codex), so a count of zero is never an
 * invented "none failed".
 */
export interface LastTurn {
  observedAt: ISOTime
  promptAt?: ISOTime
  prompt?: string
  replyAt?: ISOTime
  reply?: string
  tool?: string
  toolFailed?: boolean
  toolCalls: number
  toolErrors?: number
  filesTouched?: string[]
  lastLine?: string
  turnOpen?: boolean
}

export interface SessionRow {
  id: ID
  kind: SessionKind
  state: SessionState
  provider: Provider
  /** Absent for ordinary work; 'LOGIN' for a provider sign-in pane.
   *
   *  The console needs it to finish what a sign-in started: the operator is sent
   *  to the pane to complete the flow, and when the pane ends the credential has
   *  to be confirmed. Without this a sign-in pane is indistinguishable from any
   *  other terminal, and confirming was left to the operator finding a button on
   *  the screen they had just been navigated away from. */
  purpose?: 'LOGIN'
  taskId?: ID
  routeId?: ID
  projectId?: ID
  hostId: ID
  accountProfileId?: ID
  modelId?: ID
  /** The reasoning effort the session was launched with, shown on the pane. */
  effort?: string
  /** The project name, when the server joins one. It is the only decorated name
   *  still read here (by sessionLabel); account and model are resolved in the
   *  browser from the cached ['accounts']/['models'] lists via usePoolLabels,
   *  so those two ids never reach the screen dressed up as names. */
  projectName?: string
  /** The tmux session name, which is what an operator types to attach on the
   *  host. The provider's own handle is deliberately withheld by the server. */
  tmuxSession?: string
  workingDir?: string
  branch?: string
  startedAt?: ISOTime
  endedAt?: ISOTime
  lastActivityAt?: ISOTime
  /** How long ago the agent was last seen *working*, as opposed to merely alive,
   *  measured on the server when the payload was made. Absent means this control
   *  plane has no view of the session — unknown, which is not the same as idle. */
  busyAgoMs?: number
  /** How long ago the agent said it had stopped for a human. Absent means it has
   *  not said so, or cannot. */
  waitingAgoMs?: number
  /** How long ago the agent said its turn was over. Absent is not "unfinished" —
   *  see reportsTurns for whether it could have said so at all. */
  doneAgoMs?: number
  /** Whether this session can report a finished turn. Carried so the interface
   *  can say why a session is quiet, never to decide that it is. */
  reportsTurns?: boolean
  /** How long ago the reporting hook last ran in this session's pane at all.
   *
   *  `reportsTurns` says the hook was installed; this says it works. Absent means
   *  Kimene has never heard from it — and for a session that claims to report,
   *  that is the difference between "nobody is waiting" and "nothing here can
   *  tell me whether anybody is". The two were the same answer until 2026-08-24,
   *  and the console spent nine days confidently rendering the wrong one. */
  signalAgoMs?: number
  /** The agent saying in its own terminal title that it is at work. Absent is
   *  not "idle" — a pane with no such signal is judged by its output. */
  working?: boolean
  /** The boundary this session is at, when it is at one an executor may be
   *  switched from; absent while it is in the middle of something. The server's
   *  answer, not a second opinion derived here. */
  safeBoundary?: string
  /** Whether a PTY client stands between the session and its tmux. */
  attached?: boolean
  waitingReason?: string
  /** How the session was started, kept on the record so it can be started the
   *  same way again — and so "this one runs without approvals" is answerable
   *  without reading a command line on the host. */
  launch?: {
    label?: string
    record?: boolean
    resume?: boolean
    bypassApprovals?: boolean
  }
  /** The session this one replaced, when it was started as a restart. */
  restartOf?: ID
  /** The exit code of a session whose command has exited and whose pane tmux is
   *  still holding. Absent means none was observed, which is not a clean zero:
   *  a session whose tmux vanished outright leaves no code at all. */
  exitStatus?: number
  /** What the session last did, when its transcript can be read. See LastTurn. */
  lastTurn?: LastTurn
  /** Written by nothing since the pivot — always 0 — and therefore never shown
   *  as a measurement; the counts an operator sees come from `lastTurn`. */
  turnCount: number
  toolCallsOk: number
  toolCallsKo: number
  createdAt: ISOTime
  updatedAt: ISOTime
  version: number
}

/** api.SessionDetailResponse: the session, plus the one thing acting on it
 *  needs. There is no Checkpoint entity server-side — no field, no route — so
 *  the inspector carries the session and whether a terminal can be proxied to
 *  it, and nothing it cannot actually populate. */
export interface SessionDetail extends SessionRow {
  /** Whether this process can proxy a terminal to the session at all. False on
   *  a deployment built without the gateway, where every pane would only fail
   *  to connect. */
  terminalAvailable: boolean
}

export function sessionLabel(session: SessionRow | undefined, sessionId: string) {
  return session?.projectName ?? session?.tmuxSession ?? sessionId.slice(0, 10)
}

/**
 * The session list, as every screen that shows sessions reads it.
 *
 * One key, so the cockpit and the dashboard cost one request between them — and
 * one definition of the cadence, because the list carries the activity light:
 * whether an agent is working is a fact with a lifetime of seconds, and a light
 * on a fifteen-second poll would spend most of its time describing the recent
 * past. React Query pauses the interval while the tab is in the background, and
 * the event stream invalidates the key when a session changes.
 */
export function useSessionsList() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<PageResponse<SessionRow>>('/sessions'),
    refetchInterval: 5_000,
  })
}

/**
 * The activity of each row in a list, as of the same instant.
 *
 * Whether an agent is working is decided by a few seconds of elapsed time, so
 * the colour has to be recomputed as time passes and not only when a payload
 * arrives — the instant a payload arrives is the instant most favourable to
 * "working", and a light only ever sampled then would sit on green. One clock
 * for the page, so the row, the tile and the pane frame answer as of the same
 * moment; and the payload's own age in the browser's hands is *added* to the
 * server's measurement rather than compared against it (see sessionActivity).
 */
export function useSessionActivity(query: UseQueryResult<PageResponse<SessionRow>>) {
  const now = useNow(1000)
  const sessionsAt = query.dataUpdatedAt
  return useCallback(
    (s?: SessionRow): SessionActivity =>
      sessionActivity({
        state: s?.state,
        kind: s?.kind,
        busyAgoMs: s?.busyAgoMs,
        waitingAgoMs: s?.waitingAgoMs,
        doneAgoMs: s?.doneAgoMs,
        reportsTurns: s?.reportsTurns,
        working: s?.working,
        staleMs: sessionsAt ? now - sessionsAt : 0,
        attached: s?.attached,
      }),
    [now, sessionsAt],
  )
}

/**
 * The detail request, shared by the terminal slot and the inspector.
 *
 * One key, so the two of them cost one request between them however many slots
 * are attached — and so the slot's "can this be attached at all" answer and the
 * inspector's summary can never disagree.
 */
export function useSessionDetail(sessionId: string) {
  return useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.get<SessionDetail>(`/sessions/${sessionId}`),
    refetchInterval: 10_000,
  })
}

/**
 * A tile on the wall: a terminal, a drag handle and a resize corner.
 *
 * Wraps TerminalSlot rather than living inside it so the pane keeps one owner. It
 * contributes three things a terminal cannot do for itself:
 *
 *   - **the grip.** Pointer-based, and on the pane's header rather than its body:
 *     xterm claims keystrokes, so arrow keys inside the canvas belong to the
 *     agent, and HTML5 drag-and-drop is already taken on this subtree by the
 *     pane's file-drop handler. dnd-kit uses pointer events and brings the
 *     keyboard sensor this codebase has nowhere else.
 *   - **the span.** Applied as `grid-column: span n`, so a wide tile is still a
 *     cell of the same auto-fit grid rather than an absolutely positioned box.
 *   - **the drag transform**, set inline per pointer event. `base.css` flattens
 *     every `transition-duration` to 0.01ms under `prefers-reduced-motion`, so a
 *     drag that followed the pointer by transition would jump rather than move.
 */
export function SortableSlot({
  pane,
  session,
  activity,
  onDetach,
  onFocus,
  onResize,
  onInspect,
  onFullscreen,
  inspecting,
  active,
}: {
  pane: WallPane
  session?: SessionRow
  activity: SessionActivity
  onDetach: () => void
  onFocus: () => void
  onResize: (colSpan: number, rowSpan: number) => void
  onInspect: () => void
  /** Raised while this pane is maximised over the viewport, and again when it
   *  is not. The page needs it because a maximised pane is no longer a tile on
   *  a wall: the fit that was holding six tiles inside a column has nothing to
   *  hold, and leaving it on would show one full-screen terminal at the type
   *  size six of them needed. */
  onFullscreen?: (full: boolean) => void
  inspecting: boolean
  active: boolean
}) {
  const t = useT()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: dragId('wall', pane.id),
  })
  // A tile off the bottom of a tall wall does not open a socket until it has been
  // looked at. See useOnceVisible for why it is once and not both ways.
  const { ref: seenRef, seen } = useOnceVisible<HTMLDivElement>()
  // Inert outside a provider, so the cockpit — which does not broadcast — pays
  // nothing and behaves exactly as before.
  const broadcast = useBroadcast()
  const listening = broadcast.listening(pane.id)

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        seenRef(node)
      }}
      className={clsx(
        'k-wall__cell',
        isDragging && 'k-wall__cell--dragging',
        broadcast.enabled && (listening ? 'k-wall__cell--listening' : 'k-wall__cell--muted'),
      )}
      // Which session this tile is. The browser tests need to tell one tile from
      // another to assert that reordering does not reconnect a terminal, and the
      // pane header only shows a truncated id. The accounts matrix carries data-r
      // and data-c for the same reason.
      data-session={pane.id}
      style={{
        gridColumn: `span ${pane.colSpan}`,
        gridRow: `span ${pane.rowSpan}`,
        // No transition: see the note above. The transform is the movement.
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
      }}
    >
      {!seen ? (
        // Not an empty box: it says which session this is and what it is doing, so
        // a wall taller than the screen still reads as a wall rather than as a
        // column of grey rectangles.
        <WallPlaceholder session={session} sessionId={pane.id} activity={activity} />
      ) : (
      <TerminalSlot
        sessionId={pane.id}
        session={session}
        activity={activity}
        onDetach={onDetach}
        onFocus={onFocus}
        onInspect={onInspect}
        onFullscreen={onFullscreen}
        inspecting={inspecting}
        active={active}
        grip={
          /* In the pane's own control row, beside maximise and close.
 
             It used to be absolutely positioned over the tile's top-left corner,
             which is exactly where the pane's identity is: measured at 1280, the
             grip's box overlapped the identity button by 17px and covered all 7px
             of its status dot. So clicking a session's activity light started a
             drag instead of opening its details, and approaching a tile painted an
             opaque hover background over the light itself. That was true in every
             layout, single included — the tile wrapper is not grid-only.
 
             A span rather than a button, because Space and Enter are dnd-kit's
             start *and* end keys and a native button activates on them too: one
             press was seen twice and every drag ended where it began. That is the
             only thing that was ever proved about placement; an earlier comment
             here also claimed the header swallowed the keypress, and it did not —
             the culprit was a duplicated Space in the test, and the header was
             never retried after that was fixed. */
          <span
            className="k-wall__grip"
            aria-label={t('live.wallMove')}
            title={t('live.wallMove')}
            {...attributes}
            {...listeners}
          >
            <Icon icon={GripVertical} size={14} />
          </span>
        }
        broadcast={
          /* Only while the mode is on. A control that says "this pane is not
             receiving" when nothing is being sent anywhere is noise, and the tile
             already carries a header full of controls that do something. */
          broadcast.enabled ? (
            <Button
              variant="ghost"
              size="sm"
              className="k-wall__listen"
              onClick={() => broadcast.toggleMuted(pane.id)}
              aria-pressed={listening}
              aria-label={listening ? t('cast.mute') : t('cast.unmute')}
              title={listening ? t('cast.mute') : t('cast.unmute')}
            >
              <Icon icon={listening ? Radio : RadioReceiver} size={15} />
            </Button>
          ) : null
        }
      />
      )}
      <SpanHandle pane={pane} onResize={onResize} />
    </div>
  )
}

/** What a tile shows before it has been scrolled to. */
function WallPlaceholder({
  session,
  sessionId,
  activity,
}: {
  session?: SessionRow
  sessionId: string
  activity: SessionActivity
}) {
  const t = useT()
  const chord = usePaneChord()
  const seat = chord.numberOf(sessionId)
  return (
    <div className="k-wall__pending">
      {/* The number is on the tile before its terminal is, because the chord
          works on it before its terminal is: Cmd+S 6 scrolls to a tile that has
          never been looked at, and a placeholder with no seat would be the one
          tile an operator could not name. */}
      {seat > 0 && (
        <span
          className={clsx('k-term__seat', 'k-mono', chord.armed && 'k-term__seat--armed')}
          title={t('terminal.seat', { n: seat })}
        >
          {seat}
        </span>
      )}
      <StatusDot
        tone={sessionActivityTone(activity)}
        live={activity === 'WORKING'}
        label={t(activityLabelKey(activity))}
      />
      <span className="k-wall__pendingname">{sessionLabel(session, sessionId)}</span>
      <span className="k-wall__pendinghint">{t('live.wallPending')}</span>
    </div>
  )
}

/**
 * The corner that changes how many tracks a tile takes.
 *
 * Committed on release, never during the drag, and this is not a nicety. A tile's
 * size *is* the remote terminal's geometry: every change raises SIGWINCH and tmux
 * repaints every cell. Measured on this fleet, every agent pane runs an
 * alternate-screen application (`alternate_on=1`), which means tmux cannot reflow
 * its history — the application redraws from scratch. Streaming sizes while the
 * pointer moves would scramble every screen being dragged past.
 *
 * So the drag only moves a ghost, and the span lands once.
 */
function SpanHandle({
  pane,
  onResize,
}: {
  pane: WallPane
  onResize: (colSpan: number, rowSpan: number) => void
}) {
  const t = useT()
  const [ghost, setGhost] = useState<{ c: number; r: number } | null>(null)

  const start = (e: ReactPointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const cell = (e.currentTarget as HTMLElement).closest('.k-wall__cell')
    if (!cell) return
    const box = cell.getBoundingClientRect()
    // One track is the tile's current size divided by the spans it already has,
    // which is the only measurement available without reading the grid itself —
    // and it is the right one, because the tracks are equal `1fr`.
    const trackW = box.width / pane.colSpan
    const trackH = box.height / pane.rowSpan
    const originX = box.left
    const originY = box.top

    // On the window, not the handle: the pointer leaves a 12px corner almost
    // immediately, and a listener on the handle would end the drag there. The
    // same reason useResizableColumns gives for the column gutters.
    const onMove = (ev: PointerEvent) => {
      const c = Math.min(Math.max(Math.round((ev.clientX - originX) / trackW), 1), SPAN_MAX)
      const r = Math.min(Math.max(Math.round((ev.clientY - originY) / trackH), 1), SPAN_MAX)
      setGhost({ c, r })
    }
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      const c = Math.min(Math.max(Math.round((ev.clientX - originX) / trackW), 1), SPAN_MAX)
      const r = Math.min(Math.max(Math.round((ev.clientY - originY) / trackH), 1), SPAN_MAX)
      setGhost(null)
      if (c !== pane.colSpan || r !== pane.rowSpan) onResize(c, r)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    // Missing from useResizableColumns, and it matters: a cancelled pointer (a
    // browser gesture, a lost device) otherwise leaves the page permanently
    // unselectable with a resize cursor.
    window.addEventListener('pointercancel', onUp)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'nwse-resize'
  }

  return (
    <>
      {ghost && (
        <div
          className="k-wall__ghost"
          aria-hidden="true"
          // Drawn over the tile at the size the span would be, so the operator
          // sees the outcome without the terminal being repainted to find out.
          style={{
            width: `${(ghost.c / pane.colSpan) * 100}%`,
            height: `${(ghost.r / pane.rowSpan) * 100}%`,
          }}
        />
      )}
      <button
        type="button"
        className="k-wall__span"
        aria-label={t('live.wallResize')}
        title={t('live.wallResize')}
        onPointerDown={start}
        // The keyboard path, because a corner you must drag is no path at all.
        // Left/Up shrink, Right/Down grow, one track at a time.
        onKeyDown={(e) => {
          const dc = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
          const dr = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0
          if (!dc && !dr) return
          e.preventDefault()
          onResize(
            Math.min(Math.max(pane.colSpan + dc, 1), SPAN_MAX),
            Math.min(Math.max(pane.rowSpan + dr, 1), SPAN_MAX),
          )
        }}
      />
    </>
  )
}

function TerminalSlot({
  sessionId,
  session,
  activity,
  onDetach,
  onFocus,
  onInspect,
  onFullscreen,
  inspecting,
  active,
  grip,
  broadcast,
}: {
  sessionId: string
  session?: SessionRow
  /** Derived once for the page, so the dot in the pane's header and the row in
   *  the list cannot answer the same question differently. */
  activity: SessionActivity
  onDetach: () => void
  onFocus: () => void
  onInspect: () => void
  /** Raised while this pane is maximised; see SortableSlot for why the page
   *  cares. */
  onFullscreen?: (full: boolean) => void
  inspecting: boolean
  active: boolean
  /** The wall's drag handle, rendered among this pane's own controls. */
  grip?: ReactNode
  /** The listening toggle, when the screen is broadcasting. */
  broadcast?: ReactNode
}) {
  const t = useT()
  const observed = session?.kind === 'OBSERVED'
  /**
   * A pane watches until it is asked to do otherwise. Taking the keyboard is a
   * button, and only that button.
   *
   * Every pane used to open in CONTROL, which is right for one terminal and
   * wrong for a wall: watching six sessions took the input channel on all six,
   * held them against anyone else who wanted to type, and ran six lease
   * heartbeats to do it. Watching needs no lease at all — output streams
   * regardless; the lease exists for input.
   *
   * That was narrowed to "the focused pane takes the keyboard", which was still
   * too wide, because focus is a click and a click is how you look at something.
   * Clicking a pane to read it, clicking its output to select a line, or tabbing
   * through the wall all took the input channel — silently, from whoever else
   * held it, on a session that may be mid-command. An operator looking at a
   * terminal had no way to only look at it.
   *
   * So `active` now means what its name says: which pane the keyboard is on and
   * which one is framed. It no longer means "and take the session". The button
   * beside it says "Devral" and does exactly that, which is the affordance that
   * was there the whole time — see requestControl in TerminalPane, which both
   * acquires the lease and reports the mode back here.
   */
  const [mode, setMode] = useState<TerminalMode>('OBSERVE')
  // Fullscreen is per-terminal: each slot maximises itself over the whole
  // viewport rather than one global switch expanding the entire wall, which is
  // never what an operator watching one session wants.
  const [full, setFull] = useState(false)
  const detail = useSessionDetail(sessionId)

  // Only while it is on, with the release doing the "and now it is not". An
  // effect that reported `full` on every change would have to be trusted to run
  // its cleanup on unmount too, and a pane closed while maximised would leave
  // the page believing a terminal was still over the screen.
  useEffect(() => {
    if (!full) return
    onFullscreen?.(true)
    return () => onFullscreen?.(false)
  }, [full, onFullscreen])

  useEffect(() => {
    if (!full) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [full])

  /**
   * Cmd+M maximises the pane the keyboard is on, and puts it back.
   *
   * On `window` rather than on the pane's element, which is where Cmd+I is
   * answered, and the difference is the point: Cmd+I acts on the pane the
   * keyboard is *inside*, so bubbling up out of the terminal is exactly the
   * right reach. This one has to work while the operator is standing in the
   * session list looking at the wall — which is when they want a pane bigger —
   * so it cannot depend on focus being in a terminal.
   *
   * `active` is what stops six panes answering one press. It is the wall's own
   * "which pane the keyboard is on and which one is framed", set by one slot at
   * a time on both screens that draw slots, so the guard is the same fact the
   * frame is drawn from rather than a second opinion about it.
   *
   * Not while somebody is writing: an accelerator must not fire out from under a
   * caret, and maximising a pane behind the dialog being typed into is the
   * clearest case of that. A terminal is deliberately not "writing" — see
   * `keyboardIsInAField`, which Cmd+S is guarded by for the same reason.
   *
   * Esc leaves, above, because that is what Esc means everywhere on this console
   * and it is already on the reference sheet. Cmd is required here where Esc is
   * not: a bare `m` inside a pane belongs to the shell.
   */
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (!e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key !== 'm' && e.key !== 'M') return
      if (keyboardIsInAField()) return
      // Called even though macOS is likely to take this chord for Window >
      // Minimise before the page ever sees it: on every platform where the key
      // does arrive, letting the browser also act on it would be worse than
      // either outcome alone.
      e.preventDefault()
      setFull((f) => !f)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active])

  // An observed session has no controllable PTY behind it. The mode is pinned
  // rather than merely defaulted, so nothing downstream can raise it.
  const effectiveMode: TerminalMode = observed ? 'OBSERVE' : mode

  // Only a definite `false` withholds the pane. While the detail is in flight
  // the terminal mounts as before and reports its own connection state, which
  // is a better first frame than a placeholder that resolves into a terminal.
  const gatewayRefused = detail.data?.terminalAvailable === false

  return (
    // onFocus bubbles from everything inside the pane, including the terminal
    // itself. Firing it while this slot is already the active one would push a
    // navigation on every keystroke, so it only fires when focus genuinely
    // moves the selection.
    <div
      className={`k-termslot ${active ? 'k-termslot--active' : ''} ${full ? 'k-termslot--full' : ''}`}
      onFocus={active ? undefined : onFocus}
    >
      {/* No read-only notice here. The pane states it, in the same words, and
          states it from mount now that the kind is handed down — so this was a
          second copy of one sentence stacked directly on the first. The pane's
          is the one to keep: it is also the terminal group's description, so it
          is read on arrival rather than only by someone who passes through it. */}

      <div className="k-termslot__pane">
        {gatewayRefused ? (
          // `terminalAvailable: false` means this process cannot proxy a
          // terminal to any session — the gateway is not built into this
          // deployment. Mounting the pane anyway produces a black rectangle
          // that reconnects forever and never says why.
          <Notice tone="warn">{t('terminal.gatewayUnavailable')}</Notice>
        ) : (
          /* The pane draws the session's only header. This slot used to draw a
             second one above it, which meant the dot, the kind and the word
             "Devral" appeared twice — so what the page knows is handed down
             instead: the session's name, and the two buttons that act on the
             slot rather than on the session. */
          <TerminalPane
            sessionId={sessionId}
            mode={effectiveMode}
            kind={session?.kind}
            // Over means the transcript is gone and the recording is the only
            // history left; the pane offers whichever exists.
            ended={
              session?.state === 'COMPLETED' ||
              session?.state === 'TERMINATED' ||
              session?.state === 'FAILED' ||
              session?.state === 'CHECKPOINTED'
            }
            title={sessionLabel(session, sessionId)}
            onInspect={onInspect}
            inspecting={inspecting}
            actions={
              <>
                {grip}
                {broadcast}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFull((f) => !f)}
                  aria-pressed={full}
                  aria-label={full ? t('common.exitFullscreen') : t('terminal.fullscreen')}
                  title={full ? t('common.exitFullscreen') : t('terminal.fullscreen')}
                >
                  <Icon icon={full ? Minimize2 : Maximize2} size={15} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDetach}
                  // Focusing the control that removes a tile is not choosing
                  // that tile, and letting it count as one broke the detach
                  // outright. A press focuses the button first; focus bubbles
                  // to the slot, which selects the session, which navigates —
                  // and the effect that keeps the route's session on the wall
                  // is passive, so it ran *after* the click had taken the pane
                  // off and put it straight back. The wall flickered to one
                  // tile and returned to two, and anything reading the
                  // arrangement in between recorded the pane just closed.
                  onFocus={(e) => e.stopPropagation()}
                  aria-label={t('terminal.close')}
                  title={t('terminal.close')}
                >
                  <Icon icon={X} size={15} />
                </Button>
              </>
            }
            // Only the selected slot may claim the keyboard. The wall no longer
            // mounts every pane in CONTROL, but focus still has to be explicit:
            // two panes that both believed they were being typed into would take
            // the browser's focus from each other on every status frame.
            focusOnLive={active}
            activity={activity}
            // Maximising is a statement about which window the session should be
            // sized for, and the server ranks it above the wall tiles watching
            // the same session — including the ones on another machine, which is
            // what used to hold a full-screen pane at a tile's width.
            primary={full}
            {...(observed ? {} : { onModeChange: setMode })}
          />
        )}
      </div>
    </div>
  )
}

/**
 * What the work area shows: the layout, the saved arrangements, and the way back
 * from a hand-made list order.
 *
 * One component because it has two homes. It belongs in the sessions column, where
 * it costs no terminal height — the row it used to occupy above the wall pushed
 * every tile down for three controls that are rarely touched. But the column
 * folds, and a control that folds away with a panel is one an operator cannot rely
 * on; this same switch spent its first life inside the telemetry rail for exactly
 * that reason. So the caller renders it above the wall instead whenever the column
 * is not there.
 *
 * The order reset travels with them because it never belonged to the wall at all:
 * it undoes an order made in the *list*, and a decision needs its way back where
 * it was taken.
 */
export function WallControls({
  layout,
  onLayout,
  presets,
  onSave,
  onLoad,
  onDelete,
  canSave,
  manualOrder,
  onResetOrder,
  extra,
}: {
  layout: LayoutMode
  onLayout: (m: LayoutMode) => void
  presets: WallPreset[]
  onSave: (name: string) => void
  onLoad: (id: string) => void
  onDelete: (id: string) => void
  canSave: boolean
  manualOrder: string[] | null
  onResetOrder: () => void
  /** Controls a screen has and the other does not — the SSH console's broadcast
   *  toggle. A slot rather than a flag, so this component never has to know what
   *  a particular screen can do. */
  extra?: ReactNode
}) {
  const t = useT()
  return (
    <>
      <Segmented
        size="sm"
        value={layout}
        onChange={onLayout}
        ariaLabel={t('live.layout')}
        options={[
          { value: 'single', label: t('live.layoutSingle') },
          { value: 'split', label: t('live.layoutSplit') },
          { value: 'grid', label: t('live.layoutGrid') },
        ]}
      />
      {/* The fit control is not here. It is a display preference, not a fourth
          layout, and among Tek/Bölünmüş/Izgara it read as one — so it sits in
          the top bar beside the density switch and the fullscreen toggle, which
          are the same kind of thing. See `terminal/wallFit.tsx` for how the
          page's state reaches a button drawn by the shell. */}
      <LayoutMenu
        presets={presets}
        onSave={onSave}
        onLoad={onLoad}
        onDelete={onDelete}
        canSave={canSave}
      />
      {extra}
      {manualOrder && (
        <button type="button" className="k-wall__reorder" onClick={onResetOrder}>
          {t('order.manual')} · {t('order.reset')}
        </button>
      )}
    </>
  )
}

/**
 * Save, restore and delete a named arrangement.
 *
 * Beside the layout switch rather than in the rail, for the reason the switch
 * itself moved: a control that disappears when a panel is folded is a control an
 * operator cannot rely on.
 *
 * A saved layout names sessions, and sessions do not last — a restart mints a new
 * id — so `loadPreset` follows `restartOf` forward before it opens anything. That
 * is what stops a layout decaying the first time the work it was built to watch
 * is restarted, which is the likeliest thing to happen to it.
 */
function LayoutMenu({
  presets,
  onSave,
  onLoad,
  onDelete,
  canSave,
}: {
  presets: WallPreset[]
  onSave: (name: string) => void
  onLoad: (id: string) => void
  onDelete: (id: string) => void
  canSave: boolean
}) {
  const t = useT()
  const [naming, setNaming] = useState(false)

  return (
    <Menu as="div" className="k-wall__menu">
      <MenuButton className="k-wall__menubtn" aria-label={t('live.wallLayouts')}>
        <Icon icon={LayoutGrid} size={14} />
        <span>{t('live.wallLayouts')}</span>
      </MenuButton>
      <MenuItems anchor="bottom start" className="k-actmenu">
        {presets.length === 0 ? (
          <div className="k-actmenu__note">{t('live.wallEmpty')}</div>
        ) : (
          presets.map((preset) => (
            <div className="k-wall__presetrow" key={preset.id}>
              <MenuItem>
                <button type="button" className="k-actmenu__item" onClick={() => onLoad(preset.id)}>
                  {preset.name}
                </button>
              </MenuItem>
              <button
                type="button"
                className="k-wall__presetdel"
                aria-label={t('live.wallDelete', { name: preset.name })}
                title={t('live.wallDelete', { name: preset.name })}
                onClick={() => onDelete(preset.id)}
              >
                <Icon icon={Trash2} size={13} />
              </button>
            </div>
          ))
        )}
        <div className="k-actmenu__sep" role="separator" />
        <MenuItem disabled={!canSave}>
          <button
            type="button"
            className="k-actmenu__item"
            disabled={!canSave}
            onClick={() => setNaming(true)}
          >
            {t('live.wallSave')}
          </button>
        </MenuItem>
        {/* Said out loud, the way the appearance panel says it: "this is local"
            is the difference between a preference and a bug report. */}
        <div className="k-actmenu__note">{t('live.wallLocal')}</div>
      </MenuItems>
      {naming && (
        <SaveLayoutDialog initial="" onSave={onSave} onClose={() => setNaming(false)} />
      )}
    </Menu>
  )
}

/**
 * The one place a layout is named.
 *
 * A field in a dialog rather than window.prompt. The prompt was one line of code
 * and the wrong one: it is unstyled, untranslatable, blocked outright in some
 * embedded contexts, and it looks like a page from another decade in the middle
 * of a console this careful about its own chrome.
 *
 * Shared by the menu and the tab strip instead of copied into both. This file
 * exists because two copies of a tile drifted within a week, and a dialog is no
 * more immune to that than a tile.
 *
 * `initial` is a seed read once, when the dialog opens — the strip uses it to
 * offer the name of the layout being worn, so that updating one is the same
 * gesture as making one. Nothing takes the field back afterwards: a prefill
 * loses to a human, and the human here is someone typing a different name.
 */
function SaveLayoutDialog({
  initial,
  onSave,
  onClose,
}: {
  initial: string
  onSave: (name: string) => void
  onClose: () => void
}) {
  const t = useT()
  const [name, setName] = useState(initial)
  const trimmed = name.trim()

  return (
    <Dialog
      title={t('live.wallSave')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!trimmed}
            onClick={() => {
              if (!trimmed) return
              onSave(trimmed)
              onClose()
            }}
          >
            {t('action.save')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <TextField
          label={t('live.wallSavePrompt')}
          hint={t('live.wallLocal')}
          value={name}
          onChange={setName}
          autoFocus
        />
      </div>
    </Dialog>
  )
}

/**
 * The saved layouts, as a strip across the top of the wall.
 *
 * The menu beside the layout switch is where an arrangement is *made*; this is
 * where one is *worn*. Separating the two is the whole point, and it comes from
 * what the wall is for: three or four panes on one screen give each agent a
 * 48-column terminal, which measurably cannot be read. The way out is not a
 * wider screen but fewer terminals at a time — two named arrangements, flipped
 * between in one click. Behind a dropdown that is three interactions per switch
 * and nobody does it; in the chrome it is one, and the wall can afford to stay
 * narrow.
 *
 * It costs a row of height, and height above the wall is not free here: three
 * rarely-touched view controls used to have a row of their own and were moved
 * into the sessions column to give the terminals 36px back, measured at 1280.
 * So the strip is drawn only once there is something to *switch between* — a
 * strip holding nothing but its own save button is a row spent on a capability
 * the menu already offers, which is the decoration the terminals cannot spare.
 * The first layout is made from the menu; from the second on, this is the way.
 *
 * The strip never claims more than it knows. A tab is marked current only while
 * the wall still matches what was saved under that name; once the operator has
 * moved a tile, the mark says so rather than going on asserting an arrangement
 * that is no longer on screen — and the way to settle it is the same + that
 * made it, which opens already holding the name.
 */
export function LayoutTabs({
  presets,
  activeId,
  dirty,
  onLoad,
  onSave,
  onDelete,
  canSave,
}: {
  presets: WallPreset[]
  /** The layout the wall came from, or null when the arrangement has no name. */
  activeId: string | null
  /** Whether that layout has been arranged away from since it was loaded. */
  dirty: boolean
  onLoad: (id: string) => void
  onSave: (name: string) => void
  onDelete: (id: string) => void
  canSave: boolean
}) {
  const t = useT()
  const [naming, setNaming] = useState(false)

  if (presets.length === 0) return null

  const activeName = presets.find((p) => p.id === activeId)?.name ?? ''

  return (
    <div className="k-ltabs" role="group" aria-label={t('live.wallLayouts')}>
      {presets.map((preset) => {
        const on = preset.id === activeId
        const drifted = on && dirty
        return (
          <div key={preset.id} className={clsx('k-ltabs__tab', on && 'k-ltabs__tab--on')}>
            <button
              type="button"
              className="k-ltabs__pick"
              aria-pressed={on}
              // The full name, because the tab truncates; the drift note instead
              // once there is one, because that is the thing an operator does
              // not already know by reading the tab.
              title={drifted ? t('live.wallDrifted') : preset.name}
              onClick={() => onLoad(preset.id)}
            >
              <span className="k-ltabs__name">{preset.name}</span>
              {/* How many terminals this layout opens — the number the whole
                  strip exists to keep small, and a machine fact, so mono. */}
              <span className="k-ltabs__count k-mono">{preset.panes.length}</span>
              {drifted && (
                <>
                  {/* Colour and a shape, never motion: `prefers-reduced-motion`
                      flattens animation and must not flatten meaning. */}
                  <span className="k-ltabs__drift" aria-hidden="true" />
                  <span className="k-visually-hidden">{t('live.wallDrifted')}</span>
                </>
              )}
            </button>
            <button
              type="button"
              className="k-ltabs__del"
              aria-label={t('live.wallDelete', { name: preset.name })}
              title={t('live.wallDelete', { name: preset.name })}
              onClick={() => onDelete(preset.id)}
            >
              <Icon icon={X} size={12} />
            </button>
          </div>
        )
      })}
      <button
        type="button"
        className="k-ltabs__add"
        disabled={!canSave}
        aria-label={t('live.wallSave')}
        title={t('live.wallSave')}
        onClick={() => setNaming(true)}
      >
        <Icon icon={Plus} size={14} />
      </button>
      {naming && (
        <SaveLayoutDialog
          // Holding the worn layout's name is what makes "update this one" and
          // "make a new one" the same control: keep the name and it is saved
          // over, type another and it is a second layout.
          initial={dirty ? activeName : ''}
          onSave={onSave}
          onClose={() => setNaming(false)}
        />
      )}
    </div>
  )
}

/** A session's live age. Ticks while running (real signal); fixed once ended.
 *  Replaces the frozen `lastActivityAt` the list used to show as "last active" —
 *  that field never advances, so a running session looked idle from birth. */
export function SessionAge({ session }: { session: SessionRow }) {
  const { locale } = useLocale()
  const elapsed = useElapsed(session.startedAt, session.endedAt)
  if (elapsed == null) return null
  // Just the duration: the live dot beside the name already says "running", so
  // the word would be repeating what the colour states, and it costs the width
  // the session name needs.
  return <span className="k-slist__age k-mono">{formatDuration(elapsed, locale)}</span>
}

/**
 * Says that a session has ended, and when.
 *
 * The list holds ended sessions for a few hours rather than dropping them the
 * moment the steward records the ending — tmux keeps the pane, so the output
 * that explains a failed launch is still there to open. That only helps if the
 * row admits what it is: before this, an ended session was distinguishable from
 * a running one by the colour of one dot, and a failed one that had been on
 * screen for an hour looked like work in progress.
 */
export function SessionEnded({ session }: { session: SessionRow }) {
  const t = useT()
  const { locale } = useLocale()
  if (!session.endedAt) return null
  const failed = session.state === 'FAILED'
  return (
    <>
      <span className="k-slist__sep">·</span>
      <span className={clsx('k-slist__ended', failed && 'k-slist__ended--failed')}>
        {t(failed ? 'live.endedFailed' : 'live.endedAgo', {
          when: formatRelative(session.endedAt, locale),
        })}
      </span>
      {/* The exit code, because "failed" on its own sends the operator to a pane
          that often cannot explain itself: these CLIs draw on the alternate
          screen and restore what was underneath as they exit, so what is left
          there is the frame from *before* the failure. The status is the one
          fact that survives, and it is a machine value, so it is mono. */}
      {session.exitStatus != null && session.exitStatus !== 0 && (
        <>
          <span className="k-slist__sep">·</span>
          <span className="k-slist__exit k-mono">
            {t('live.exitStatus', { code: String(session.exitStatus) })}
          </span>
        </>
      )}
    </>
  )
}
